import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { chartOfAccountsApi } from '../../services/financeApi';

/**
 * Searchable ledger account dropdown with infinite scroll pagination.
 * Fetches from GET /api/finance/chart-of-accounts/dropdown (active accounts only).
 *
 * Props:
 *   value        — currently selected account ID (string)
 *   onChange     — (accountId: string, displayLabel: string) => void
 *   displayLabel — pre-filled display label for edit mode (e.g. "1200 - Cash")
 *   accountTypeId — optional filter by account type
 *   accountTypeName — optional substring match on Finance AccountType.name (case-insensitive)
 *   ledgerGroup — optional semantic bucket: ASSETS | LIABILITIES | EQUITY | REVENUE | EXPENSES | OCI
 *   categoryId   — optional filter by category
 *   disabled     — boolean
 *   error        — boolean, renders red border
 *   placeholder  — string
 *   menuPlacement — 'auto' | 'up' | 'down' (default auto)
 */
const SearchableLedgerDropdown = ({
  value,
  onChange,
  displayLabel = '',
  loading: parentLoading = false,
  accountTypeId,
  accountTypeName,
  ledgerGroup,
  categoryId,
  normalSide,
  accountTypeCategory,
  accountClassification,
  disabled = false,
  error = false,
  placeholder = 'Select account',
  limit = 50,
  menuPlacement = 'auto'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState(displayLabel);
  const [dropdownStyle, setDropdownStyle] = useState({ position: 'fixed', top: 0, left: 0, width: 0, zIndex: 10000, visibility: 'hidden' });

  const containerRef = useRef(null);
  const menuRef = useRef(null);
  const dropdownUidRef = useRef(null);
  const listRef = useRef(null);
  const searchTimerRef = useRef(null);
  const fetchingRef = useRef(false);
  const currentSearchRef = useRef('');

  const computeDropdownStyle = useCallback(() => {
    if (!containerRef.current) return;

    const rect = containerRef.current.getBoundingClientRect();
    const gap = 4;
    const viewportPadding = 8;
    // Cap height so upward menus sit next to the trigger instead of stretching to the page top.
    const preferredMenuHeight = 280;
    const measuredMenuHeight = menuRef.current?.offsetHeight || 0;
    const estimatedMenuHeight = measuredMenuHeight || preferredMenuHeight;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const spaceAbove = rect.top - viewportPadding;

    let placeDown;
    if (menuPlacement === 'down') {
      placeDown = true;
    } else if (menuPlacement === 'up') {
      placeDown = false;
    } else if (spaceBelow >= Math.min(preferredMenuHeight, 220)) {
      placeDown = true;
    } else if (spaceAbove >= Math.min(preferredMenuHeight, 220)) {
      placeDown = false;
    } else {
      placeDown = spaceBelow >= spaceAbove;
    }

    // Match the trigger width exactly so the panel doesn't look detached.
    const width = Math.max(rect.width, 200);
    const maxLeft = Math.max(viewportPadding, window.innerWidth - width - viewportPadding);
    const left = Math.min(Math.max(viewportPadding, rect.left), maxLeft);

    const spaceForMenu = placeDown ? spaceBelow - gap : spaceAbove - gap;
    const maxAvailableHeight = Math.max(160, Math.min(preferredMenuHeight, spaceForMenu));
    const menuHeightForPosition = Math.min(estimatedMenuHeight, maxAvailableHeight);
    const top = placeDown
      ? rect.bottom + gap
      : Math.max(viewportPadding, rect.top - menuHeightForPosition - gap);

    setDropdownStyle({
      position: 'fixed',
      top,
      left,
      width,
      zIndex: 10000,
      maxHeight: maxAvailableHeight,
      visibility: 'visible'
    });
  }, [menuPlacement]);

  // Sync displayLabel from parent on edit mode
  useEffect(() => {
    if (displayLabel) {
      setSelectedLabel(displayLabel);
    } else if (!value) {
      setSelectedLabel('');
    }
  }, [displayLabel, value]);

  const fetchAccounts = useCallback(async (searchTerm, page, append) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);
    try {
      const params = { page, limit: 50 };
      if (searchTerm) params.search = searchTerm;
      if (accountTypeId) params.accountTypeId = accountTypeId;
      if (ledgerGroup) params.ledgerGroup = ledgerGroup;
      if (accountTypeName) params.accountTypeName = accountTypeName;
      if (categoryId) params.categoryId = categoryId;
      if (normalSide) params.normalSide = normalSide;
      if (accountTypeCategory) params.accountTypeCategory = accountTypeCategory;
      if (accountClassification) params.accountClassification = accountClassification;
      params.limit = limit;

      const response = await chartOfAccountsApi.getAccountsDropdown(params);
      const { accounts: newAccounts = [], pagination: pag = {} } =
        response.data?.data || {};
      setAccounts(prev => append ? [...prev, ...newAccounts] : newAccounts);
      setPagination({
        page: pag.page || 1,
        pages: pag.pages || 1,
        total: pag.total || 0
      });
    } catch (err) {
      console.error('SearchableLedgerDropdown: fetch error', err);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [accountTypeId, accountTypeName, ledgerGroup, categoryId, normalSide, accountTypeCategory, accountClassification, limit]);

  const openDropdown = () => {
    if (disabled || parentLoading) return;
    setIsOpen(true);
    document.dispatchEvent(new CustomEvent('slim:dropdownOpened', { detail: dropdownUidRef }));
    if (accounts.length === 0 && !loading) {
      currentSearchRef.current = '';
      fetchAccounts('', 1, false);
    }
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const update = () => computeDropdownStyle();
    // Position after mount, then again after content paints.
    const raf1 = window.requestAnimationFrame(() => {
      update();
      window.requestAnimationFrame(update);
    });

    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.cancelAnimationFrame(raf1);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [isOpen, accounts.length, loading, pagination.total, computeDropdownStyle]);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      currentSearchRef.current = val;
      setAccounts([]);
      setPagination({ page: 1, pages: 1, total: 0 });
      fetchAccounts(val, 1, false);
    }, 300);
  };

  const handleScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 40;
    if (nearBottom && !loading && !fetchingRef.current && pagination.page < pagination.pages) {
      fetchAccounts(currentSearchRef.current, pagination.page + 1, true);
    }
  };

  // Click outside to close — menu is portaled to body, so check both refs
  useEffect(() => {
    if (!isOpen) return undefined;

    const handler = (e) => {
      const inTrigger = containerRef.current?.contains(e.target);
      const inMenu = menuRef.current?.contains(e.target);
      if (!inTrigger && !inMenu) {
        setIsOpen(false);
        setSearch('');
      }
    };
    const handleOtherOpen = (e) => {
      if (e.detail !== dropdownUidRef) {
        setIsOpen(false);
        setSearch('');
      }
    };

    document.addEventListener('mousedown', handler);
    document.addEventListener('slim:dropdownOpened', handleOtherOpen);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('slim:dropdownOpened', handleOtherOpen);
    };
  }, [isOpen]);

  const handleSelect = (account) => {
    const label = `${account.code} - ${account.name}`;
    setSelectedLabel(label);
    onChange(String(account._id), label, account);
    setIsOpen(false);
    setSearch('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    setSelectedLabel('');
    setAccounts([]);
    onChange('', '', null);
  };

  const hasMore = pagination.page < pagination.pages;
  const listMaxHeight = Math.max(120, (dropdownStyle.maxHeight || 280) - 72);

  const menu = isOpen
    ? createPortal(
        <div
          ref={menuRef}
          style={{
            ...dropdownStyle,
            backgroundColor: '#ffffff'
          }}
          className="border border-gray-200 rounded-lg shadow-xl overflow-hidden"
        >
          <div className="p-2 border-b border-gray-100 bg-white">
            <div className="relative">
              <svg
                className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-gray-400 pointer-events-none"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                autoFocus
                type="text"
                value={search}
                onChange={handleSearchChange}
                placeholder="Search by code or name..."
                className="w-full pl-6 pr-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-gray-50"
              />
            </div>
            {pagination.total > 0 && (
              <p className="text-xs text-gray-400 mt-1">
                {pagination.total} account{pagination.total !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          <div
            ref={listRef}
            onScroll={handleScroll}
            className="overflow-y-auto bg-white"
            style={{ maxHeight: listMaxHeight, backgroundColor: '#ffffff' }}
          >
            {!loading && accounts.length === 0 && (
              <p className="px-3 py-4 text-xs text-gray-500 text-center">
                {search ? 'No accounts match your search' : 'No accounts found'}
              </p>
            )}

            {accounts.map((account) => {
              const isSelected = String(account._id) === String(value);
              return (
                <button
                  key={account._id}
                  type="button"
                  onClick={() => handleSelect(account)}
                  className={`w-full flex flex-col px-3 py-1.5 text-left transition-colors ${
                    isSelected
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-900 hover:bg-gray-50'
                  }`}
                >
                  <span className="text-sm font-medium leading-tight">
                    {account.code} - {account.name}
                  </span>
                  {(account.accountType?.name || account.category?.name) && (
                    <span className="text-xs text-gray-400 leading-tight truncate">
                      {[account.accountType?.name, account.category?.name]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  )}
                </button>
              );
            })}

            {loading && (
              <div className="flex items-center justify-center py-3">
                <svg className="animate-spin w-3 h-3 text-blue-600 mr-1.5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                </svg>
                <span className="text-xs text-gray-500">Loading...</span>
              </div>
            )}

            {!loading && hasMore && (
              <p className="text-center text-xs text-gray-400 py-1.5 border-t border-gray-100">
                Scroll for more results
              </p>
            )}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className="relative w-full" ref={containerRef}>
      <button
        type="button"
        onClick={openDropdown}
        disabled={disabled || parentLoading}
        className={`w-full px-2 py-1 text-sm border rounded flex items-center justify-between text-left focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
          error ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
        } ${disabled || parentLoading ? 'opacity-70 cursor-not-allowed' : 'hover:border-blue-400 cursor-pointer'}`}
      >
        <span className={`truncate flex items-center ${selectedLabel ? 'text-gray-900' : 'text-gray-400'}`}>
          {parentLoading && !selectedLabel ? (
            <span className="inline-flex items-center text-blue-600 font-normal">
              <svg className="animate-spin -ml-0.5 mr-1.5 h-3.5 w-3.5 text-blue-600" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Loading account...
            </span>
          ) : (
            selectedLabel || placeholder
          )}
        </span>
        <div className="flex items-center gap-1 flex-shrink-0 ml-1">
          {selectedLabel && !disabled && !parentLoading && (
            <span
              onClick={handleClear}
              className="text-gray-300 hover:text-gray-500 leading-none text-sm"
              title="Clear selection"
            >
              ×
            </span>
          )}
          <svg
            className={`w-3 h-3 text-gray-400 transition-transform flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {menu}
    </div>
  );
};

export default SearchableLedgerDropdown;
